// Drawer for creating + editing a Customer.
//
// Extracted from CustomersModule when the page was rewritten as the
// Customers Overview dashboard+list. The list page mounts this drawer for
// "+ New customer" and the per-row edit pencil; nothing else uses it yet.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Pencil, Trash2, Mail, Phone, Globe, X, Check,
  Activity, Copy, RefreshCw,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  Customer, Contact, CustomerDetail, MonitoringCustomerTab, User,
} from '@/types/api'
import { INVOICE_DETAIL_LEVELS } from '@/types/api'
import { StatusBadge } from '@/components/primitives/StatusBadge'

const CONTACT_ROLES: { value: Contact['role']; label: string }[] = [
  { value: 'primary',     label: 'Primary'     },
  { value: 'technical',   label: 'Technical'   },
  { value: 'billing',     label: 'Billing'     },
  { value: 'stakeholder', label: 'Stakeholder' },
]

export interface CustomerDrawerProps {
  customer: Customer | null
  onClose:  () => void
  onSaved:  () => void
}

interface CustomerForm {
  name:              string
  orgNr:             string
  billingEmail:      string
  paymentTerms:      string
  currency:          string
  website:           string
  street:            string
  city:              string
  postalCode:        string
  country:           string
  tenantId:          string
  hasMsa:            boolean
  msaSignedDate:     string
  hasNda:            boolean
  ndaSignedDate:     string
  hasDpa:            boolean
  dpaSignedDate:     string
  devopsOrgUrl:      string
  teamsUrl:          string
  sharepointUrl:     string
  notes:             string
  invoiceDetailLevel: string
  accountManagerId:  number | null
}

function toForm(c: Customer | null): CustomerForm {
  return {
    name:              c?.name              ?? '',
    orgNr:             c?.orgNr             ?? '',
    billingEmail:      c?.billingEmail      ?? '',
    paymentTerms:      c?.paymentTerms      ?? '',
    currency:          c?.currency          ?? 'SEK',
    website:           c?.website           ?? '',
    street:            c?.street            ?? '',
    city:              c?.city              ?? '',
    postalCode:        c?.postalCode        ?? '',
    country:           c?.country           ?? 'Sweden',
    tenantId:          c?.tenantId          ?? '',
    hasMsa:            c?.hasMsa            ?? false,
    msaSignedDate:     c?.msaSignedDate     ?? '',
    hasNda:            c?.hasNda            ?? false,
    ndaSignedDate:     c?.ndaSignedDate     ?? '',
    hasDpa:            c?.hasDpa            ?? false,
    dpaSignedDate:     c?.dpaSignedDate     ?? '',
    devopsOrgUrl:      c?.devopsOrgUrl      ?? '',
    teamsUrl:          c?.teamsUrl          ?? '',
    sharepointUrl:     c?.sharepointUrl     ?? '',
    notes:             c?.notes             ?? '',
    invoiceDetailLevel: c?.invoiceDetailLevel ?? 'summary',
    accountManagerId:  c?.accountManagerId  ?? null,
  }
}

export function CustomerDrawer({ customer, onClose, onSaved }: CustomerDrawerProps) {
  const isEdit = customer !== null
  const queryClient = useQueryClient()

  const [drawerTab, setDrawerTab] = useState<'details' | 'monitoring'>('details')
  const [form, setForm] = useState<CustomerForm>(() => toForm(customer))
  const set = (key: keyof CustomerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }))
  const setCheck = (key: keyof CustomerForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.checked }))

  const { data: detail } = useQuery({
    queryKey: ['customers', customer?.id, 'detail'],
    queryFn:  () => api.get<CustomerDetail>(`/customers/${customer?.id}`),
    enabled:  isEdit,
  })
  const contacts = detail?.contacts ?? []

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  () => api.get<User[]>('/users'),
  })

  const saveMutation = useMutation({
    mutationFn: (body: CustomerForm) =>
      isEdit
        ? api.patch(`/customers/${customer?.id}`, body)
        : api.post<{ id: number }>('/customers', body),
    onSuccess: onSaved,
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/customers/${customer?.id}`),
    onSuccess:  onSaved,
  })

  const handleSave = () => {
    if (!form.name.trim()) return
    saveMutation.mutate(form)
  }

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: 'var(--ns-surface)', borderLeft: '1px solid var(--ns-border)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ns-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>{isEdit ? customer?.name : 'New customer'}</h2>
            {isEdit && customer?.orgNr && (
              <div className="muted mono" style={{ fontSize: 12 }}>{customer.orgNr}</div>
            )}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        {isEdit && (
          <div className="tabs" style={{ padding: '0 24px', borderBottom: '1px solid var(--ns-border)', flexShrink: 0 }}>
            <button
              className={`tab ${drawerTab === 'details' ? 'active' : ''}`}
              onClick={() => setDrawerTab('details')}
            >
              Details
            </button>
            <button
              className={`tab ${drawerTab === 'monitoring' ? 'active' : ''}`}
              onClick={() => setDrawerTab('monitoring')}
            >
              <Activity size={12} />Monitoring
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>

          {drawerTab === 'monitoring' && isEdit && customer && (
            <MonitoringTab customerId={customer.id} tenantId={customer.tenantId} />
          )}

          {drawerTab === 'details' && (<>

          <Section label="General">
            <Field label="Name *">
              <input className="input" style={{ width: '100%' }} value={form.name} onChange={set('name')} autoFocus placeholder="Company name" />
            </Field>
            <Row>
              <Field label="Org number">
                <input className="input mono" style={{ width: '100%' }} value={form.orgNr} onChange={set('orgNr')} placeholder="556000-0000" />
              </Field>
              <Field label="Fortnox #">
                <input className="input mono" style={{ width: '100%' }} value={customer?.fortnoxCustomerNumber ?? ''} disabled placeholder="Synced from Fortnox" />
              </Field>
            </Row>
            <Field label="Website">
              <div style={{ position: 'relative' }}>
                <Globe size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)', pointerEvents: 'none' }} />
                <input className="input" style={{ width: '100%', paddingLeft: 30 }} value={form.website} onChange={set('website')} placeholder="https://example.com" />
              </div>
            </Field>
          </Section>

          <Section label="Billing">
            <Field label="Billing email">
              <div style={{ position: 'relative' }}>
                <Mail size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)', pointerEvents: 'none' }} />
                <input className="input" style={{ width: '100%', paddingLeft: 30 }} value={form.billingEmail} onChange={set('billingEmail')} placeholder="invoice@customer.com" />
              </div>
            </Field>
            <Row>
              <Field label="Payment terms">
                <input className="input" style={{ width: '100%' }} value={form.paymentTerms} onChange={set('paymentTerms')} placeholder="30 netto" />
              </Field>
              <Field label="Currency">
                <select className="input" style={{ width: '100%' }} value={form.currency} onChange={set('currency')}>
                  {['SEK', 'EUR', 'USD', 'NOK', 'DKK'].map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </Row>
            <Field label="Invoice detail level">
              <select className="input" style={{ width: '100%', fontSize: 13 }} value={form.invoiceDetailLevel} onChange={set('invoiceDetailLevel')}>
                {INVOICE_DETAIL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Field>
          </Section>

          <Section label="Ownership">
            <Field label="Account manager">
              <select
                className="input"
                style={{ width: '100%', fontSize: 13 }}
                value={form.accountManagerId ?? ''}
                onChange={e => setForm(prev => ({
                  ...prev,
                  accountManagerId: e.target.value ? Number(e.target.value) : null,
                }))}
              >
                <option value="">— none —</option>
                {users
                  .filter(u => u.isActive)
                  .map(u => <option key={u.id} value={u.id}>{u.displayName}</option>)}
              </select>
            </Field>
          </Section>

          <Section label="Address">
            <Field label="Street">
              <input className="input" style={{ width: '100%' }} value={form.street} onChange={set('street')} placeholder="Street address" />
            </Field>
            <Row>
              <Field label="City">
                <input className="input" style={{ width: '100%' }} value={form.city} onChange={set('city')} placeholder="Stockholm" />
              </Field>
              <Field label="Postal code">
                <input className="input mono" style={{ width: '100%' }} value={form.postalCode} onChange={set('postalCode')} placeholder="111 22" />
              </Field>
            </Row>
            <Field label="Country">
              <input className="input" style={{ width: '100%' }} value={form.country} onChange={set('country')} />
            </Field>
          </Section>

          <Section label="Workspace links">
            <Field label="Microsoft tenant ID">
              <input className="input mono" style={{ width: '100%', fontSize: 12 }} value={form.tenantId} onChange={set('tenantId')} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </Field>
            <Field label="Azure DevOps org">
              <input className="input" style={{ width: '100%' }} value={form.devopsOrgUrl} onChange={set('devopsOrgUrl')} placeholder="https://dev.azure.com/org" />
            </Field>
            <Row>
              <Field label="Teams workspace">
                <input className="input" style={{ width: '100%' }} value={form.teamsUrl} onChange={set('teamsUrl')} placeholder="Teams URL" />
              </Field>
              <Field label="SharePoint">
                <input className="input" style={{ width: '100%' }} value={form.sharepointUrl} onChange={set('sharepointUrl')} placeholder="SharePoint URL" />
              </Field>
            </Row>
          </Section>

          <Section label="Legal & compliance">
            <LegalRow
              label="MSA" sublabel="Master Service Agreement"
              checked={form.hasMsa} onCheck={setCheck('hasMsa')}
              date={form.msaSignedDate} onDate={set('msaSignedDate')}
            />
            <LegalRow
              label="NDA" sublabel="Non-Disclosure Agreement"
              checked={form.hasNda} onCheck={setCheck('hasNda')}
              date={form.ndaSignedDate} onDate={set('ndaSignedDate')}
            />
            <LegalRow
              label="DPA" sublabel="GDPR Data Processing Agreement"
              checked={form.hasDpa} onCheck={setCheck('hasDpa')}
              date={form.dpaSignedDate} onDate={set('dpaSignedDate')}
            />
          </Section>

          <Section label="Notes">
            <textarea
              className="input"
              style={{ width: '100%', resize: 'vertical', minHeight: 80 }}
              value={form.notes}
              onChange={set('notes')}
              placeholder="Internal notes about this customer…"
              rows={3}
            />
          </Section>

          {isEdit && customer && (
            <Section label={`Contacts${contacts.length > 0 ? ` (${contacts.length})` : ''}`}>
              <ContactsSection customerId={customer.id} contacts={contacts} onChanged={() =>
                queryClient.invalidateQueries({ queryKey: ['customers', customer.id, 'detail'] })
              } />
            </Section>
          )}

          </>)}

        </div>

        {drawerTab === 'details' && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8 }}>
            {isEdit && (
              <button
                className="btn btn-ghost"
                style={{ color: 'var(--ns-err)', marginRight: 'auto' }}
                onClick={() => {
                  if (confirm(`Deactivate "${customer?.name}"?`)) deleteMutation.mutate()
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 size={13} />Deactivate
              </button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-brand"
              onClick={handleSave}
              disabled={!form.name.trim() || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create customer'}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

// ─── Monitoring tab ───────────────────────────────────────────────────────────

function MonitoringTab({ customerId, tenantId }: { customerId: number; tenantId: string | null }) {
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['monitoring', 'customer-tab', customerId],
    queryFn:  () => api.get<MonitoringCustomerTab>(`/monitoring/customer-tab/${customerId}`),
    enabled:  !!tenantId,
  })

  const regenerateMutation = useMutation({
    mutationFn: () => api.post(`/monitoring/customer-tab/${customerId}/apim-regenerate`, {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['monitoring', 'customer-tab', customerId] }),
  })

  if (!tenantId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ns-text-muted)' }}>
        <Activity size={28} style={{ opacity: 0.3, marginBottom: 10 }} />
        <div style={{ fontSize: 13, fontWeight: 500 }}>No tenant ID</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Set a Microsoft Tenant ID in the Details tab to link monitoring data.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
  }

  if (!data?.enrolled) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ns-text-muted)' }}>
        <Activity size={28} style={{ opacity: 0.3, marginBottom: 10 }} />
        <div style={{ fontSize: 13, fontWeight: 500 }}>Not enrolled in Monitoring</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          This customer has not been provisioned in Numberskills Monitoring.
        </div>
      </div>
    )
  }

  const tierColor: Record<string, string> = {
    Enterprise: 'var(--ns-brand)',
    Standard:   'var(--ns-ok)',
    Trial:      'var(--ns-warn)',
  }

  const copy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {(data.apim?.tier ?? data.contractType) && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
            background: `oklch(from ${tierColor[data.apim?.tier ?? data.contractType ?? ''] ?? 'var(--ns-text-muted)'} l c h / 0.12)`,
            color: tierColor[data.apim?.tier ?? data.contractType ?? ''] ?? 'var(--ns-text-muted)',
            border: `1px solid oklch(from ${tierColor[data.apim?.tier ?? data.contractType ?? ''] ?? 'var(--ns-border)'} l c h / 0.3)`,
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            {data.apim?.tier ?? data.contractType}
          </span>
        )}
        {data.onboardedAt && (
          <span className="muted" style={{ fontSize: 12 }}>
            Enrolled {data.onboardedAt.slice(0, 10)}
          </span>
        )}
        {data.onboardingStatus && (
          <span className="badge">{data.onboardingStatus}</span>
        )}
      </div>

      {data.apim && (
        <Section label="APIM subscription">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              className="input mono"
              style={{ flex: 1, fontSize: 12, color: 'var(--ns-text-muted)' }}
              value={data.apim.keyMasked}
              readOnly
            />
            <button
              className="btn btn-ghost btn-icon"
              title="Copy key"
              onClick={() => copy(data.apim?.keyMasked ?? '')}
            >
              {copied ? <Check size={13} style={{ color: 'var(--ns-ok)' }} /> : <Copy size={13} />}
            </button>
            <button
              className="btn btn-ghost"
              title="Regenerate key"
              disabled={regenerateMutation.isPending}
              onClick={() => {
                if (confirm('Regenerate APIM key? The old key will stop working immediately.')) {
                  regenerateMutation.mutate()
                }
              }}
            >
              <RefreshCw size={13} />
              {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {data.apim.subscriptionName && <span className="mono">{data.apim.subscriptionName}</span>}
            {data.apim.totalRequests != null && <span> · {data.apim.totalRequests.toLocaleString()} total requests</span>}
            {data.apim.lastUsedAt && <span> · Last used {data.apim.lastUsedAt.slice(0, 10)}</span>}
          </div>
        </Section>
      )}

      {data.events30d && (
        <Section label="Events (30 days)">
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{data.events30d.total}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>Total</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono', color: 'var(--ns-ok)' }}>{data.events30d.success}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>Success</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono', color: 'var(--ns-err)' }}>{data.events30d.failed}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>Failed</div>
            </div>
          </div>
        </Section>
      )}

      {data.recentEvents.length > 0 && (
        <Section label="Recent runs">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.recentEvents.map(e => (
              <div key={e.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 0', borderBottom: '1px solid var(--ns-border)',
              }}>
                <StatusBadge status={e.statusNorm} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono" style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.item_name}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{e.workspace_name}</div>
                </div>
                <div className="mono muted" style={{ fontSize: 11, flexShrink: 0 }}>{e.durationFormatted}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {data.workspaces.length > 0 && (
        <Section label={`Workspaces (${data.workspaces.length})`}>
          {data.workspaces.map(w => (
            <div key={w.workspace_id} style={{
              padding: '8px 10px', borderRadius: 6, background: 'var(--ns-surface-2)',
              marginBottom: 6,
            }}>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{w.workspace_name}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                {w.region && <span>{w.region} · </span>}
                <span className="mono">{w.workspace_id}</span>
              </div>
            </div>
          ))}
        </Section>
      )}

    </div>
  )
}

// ─── Contacts section ─────────────────────────────────────────────────────────

interface ContactsSectionProps {
  customerId: number
  contacts:   Contact[]
  onChanged:  () => void
}

function ContactsSection({ customerId, contacts, onChanged }: ContactsSectionProps) {
  const [editing,    setEditing]    = useState<Contact | null>(null)
  const [addingNew,  setAddingNew]  = useState(false)

  const addMutation = useMutation({
    mutationFn: (body: Partial<Contact>) => api.post(`/customers/${customerId}/contacts`, body),
    onSuccess:  () => { onChanged(); setAddingNew(false) },
  })

  const updateMutation = useMutation({
    mutationFn: (body: Partial<Contact> & { id: number }) =>
      api.put(`/customers/${customerId}/contacts/${body.id}`, body),
    onSuccess:  () => { onChanged(); setEditing(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/customers/${customerId}/contacts/${id}`),
    onSuccess:  onChanged,
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {contacts.map(c =>
        editing?.id === c.id ? (
          <ContactForm
            key={c.id}
            initial={c}
            onSave={data => updateMutation.mutate({ ...data, id: c.id })}
            onCancel={() => setEditing(null)}
            isPending={updateMutation.isPending}
          />
        ) : (
          <div key={c.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '8px 10px', borderRadius: 6, background: 'var(--ns-surface-2)',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                {c.isPrimary && <span className="badge badge-ok" style={{ fontSize: 10 }}>primary</span>}
                <span className="badge" style={{ fontSize: 10 }}>{c.role}</span>
              </div>
              {c.title && <div className="muted" style={{ fontSize: 12 }}>{c.title}</div>}
              <div style={{ display: 'flex', gap: 12, marginTop: 2 }}>
                {c.email && (
                  <a href={`mailto:${c.email}`} style={{ fontSize: 12, color: 'var(--ns-brand)', display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <Mail size={11} />{c.email}
                  </a>
                )}
                {c.phone && (
                  <span style={{ fontSize: 12, color: 'var(--ns-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Phone size={11} />{c.phone}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              <button className="btn btn-ghost btn-icon" title="Edit" onClick={() => setEditing(c)}>
                <Pencil size={12} />
              </button>
              <button className="btn btn-ghost btn-icon" title="Delete" style={{ color: 'var(--ns-err)' }}
                onClick={() => { if (confirm(`Remove ${c.name}?`)) deleteMutation.mutate(c.id) }}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        )
      )}

      {addingNew ? (
        <ContactForm
          onSave={data => addMutation.mutate(data)}
          onCancel={() => setAddingNew(false)}
          isPending={addMutation.isPending}
        />
      ) : (
        <button className="btn btn-ghost" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={() => setAddingNew(true)}>
          <Plus size={13} />Add contact
        </button>
      )}
    </div>
  )
}

interface ContactFormProps {
  initial?:   Partial<Contact>
  onSave:     (data: Partial<Contact>) => void
  onCancel:   () => void
  isPending:  boolean
}

function ContactForm({ initial, onSave, onCancel, isPending }: ContactFormProps) {
  const [name,      setName]      = useState(initial?.name      ?? '')
  const [email,     setEmail]     = useState(initial?.email     ?? '')
  const [phone,     setPhone]     = useState(initial?.phone     ?? '')
  const [title,     setTitle]     = useState(initial?.title     ?? '')
  const [role,      setRole]      = useState<Contact['role']>(initial?.role ?? 'stakeholder')
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary ?? false)

  return (
    <div style={{
      padding: 12, borderRadius: 6, border: '1px solid var(--ns-border)',
      background: 'var(--ns-surface)', display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <Row>
        <Field label="Name *">
          <input className="input" style={{ width: '100%' }} value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Full name" />
        </Field>
        <Field label="Role">
          <select className="input" style={{ width: '100%' }} value={role} onChange={e => setRole(e.target.value as Contact['role'])}>
            {CONTACT_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="Email">
          <input className="input" style={{ width: '100%' }} value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
        </Field>
        <Field label="Phone">
          <input className="input" style={{ width: '100%' }} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+46 70 000 00 00" />
        </Field>
      </Row>
      <Field label="Job title">
        <input className="input" style={{ width: '100%' }} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. CFO" />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} />
        Primary contact
      </label>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onCancel} style={{ fontSize: 12 }}>Cancel</button>
        <button
          className="btn btn-brand"
          style={{ fontSize: 12 }}
          disabled={!name.trim() || isPending}
          onClick={() => onSave({ name, email, phone, title, role, isPrimary })}
        >
          {isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ─── Layout helpers ───────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--ns-text-muted)', marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 10 }}>{children}</div>
}

function LegalRow({
  label, sublabel, checked, onCheck, date, onDate,
}: {
  label: string; sublabel: string
  checked: boolean; onCheck: (e: React.ChangeEvent<HTMLInputElement>) => void
  date: string; onDate: (e: React.ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
        <input type="checkbox" checked={checked} onChange={onCheck} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
          <div className="muted" style={{ fontSize: 11 }}>{sublabel}</div>
        </div>
      </label>
      {checked && (
        <div>
          <div className="muted" style={{ fontSize: 11, marginBottom: 3 }}>Signed date</div>
          <input type="date" className="input" style={{ fontSize: 12 }} value={date} onChange={onDate} />
        </div>
      )}
    </div>
  )
}
